import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../style/AdminImportantLinks.css";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import { AbstractAPI } from "../api";

const AdminImportantLinks = () => {
  const navigate = useNavigate();

  const [links, setLinks] = useState([]);
  const [formData, setFormData] = useState({
    id: null,
    heading: "",
    title: "",
    url: "",
  });

  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showHeadingDropdown, setShowHeadingDropdown] = useState(false);
  const [showTitleDropdown, setShowTitleDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 12;
  const [searchQuery, setSearchQuery] = useState("");

  const paginatedLinks = links.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const data = await AbstractAPI.getAllImportantLinks();
      setLinks(data);
    } catch (err) {
      console.error("Failed to load links", err);
    }
  };
  /* ================= DERIVED DATA ================= */
  const headings = [...new Set(links.map((l) => l.heading))];

  const filteredHeadings = headings.filter((h) =>
    h.toLowerCase().includes(formData.heading.toLowerCase()),
  );

  const titlesByHeading = links.filter((l) => l.heading === formData.heading);

  const validate = () => {
    const e = {};
    const urlRegex = /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/.*)?$/i;

    if (!formData.heading.trim()) e.heading = "Heading is required";
    if (!formData.title.trim()) e.title = "Link title is required";
    if (!formData.url.trim()) e.url = "URL is required";
    else if (!urlRegex.test(formData.url)) e.url = "Invalid URL format";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const payload = {
        heading: formData.heading,
        title: formData.title,
        url: formData.url,
      };

      let data;

      if (editing) {
        data = await AbstractAPI.updateImportantLink(formData.id, payload);
        setLinks(links.map((l) => (l.id === data.id ? data : l)));
      } else {
        data = await AbstractAPI.createImportantLink(payload);
        setLinks([data, ...links]);
      }

      resetForm();
    } catch (err) {
      console.error("Save failed", err);
    }
    setPage(1);
  };

  const resetForm = () => {
    setFormData({ id: null, heading: "", title: "", url: "" });
    setEditing(false);
    setErrors({});
    setShowHeadingDropdown(false);
    setShowTitleDropdown(false);
  };

  const confirmDelete = async () => {
    try {
      await AbstractAPI.deleteImportantLink(deleteTarget.id);
      setLinks(links.filter((l) => l.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Delete failed", err);
    }
    setPage(1);
  };

  return (
    <div className="admin-links-page">
      {/* HEADER */}
      <div className="page-header">
        <button className="clear-filters-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h2>Manage Important Links</h2>
      </div>

      {/* FORM */}
      <form className="adminformimportant" onSubmit={handleSubmit}>
        {/* HEADING */}
        <div className="form-group">
          <label>Heading</label>
          <input
            value={formData.heading}
            className={errors.heading ? "error-input" : ""}
            placeholder="Search or add heading"
            onChange={(e) => {
              setFormData({
                ...formData,
                heading: e.target.value,
                title: "",
                url: "",
              });
              setShowHeadingDropdown(true);
            }}
            onFocus={() => setShowHeadingDropdown(true)}
          />
          {errors.heading && <span className="error">{errors.heading}</span>}

          {showHeadingDropdown && filteredHeadings.length > 0 && (
            <ul className="admin-il-dropdown">
              {filteredHeadings.map((h, i) => (
                <li
                  key={i}
                  onClick={() => {
                    setFormData({
                      ...formData,
                      heading: h,
                      title: "",
                      url: "",
                    });
                    setShowHeadingDropdown(false);
                  }}
                >
                  {h}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* TITLE */}
        <div className="form-group">
          <label>Link Title</label>
          <input
            value={formData.title}
            className={errors.title ? "error-input" : ""}
            placeholder="Select or add title"
            onFocus={() => setShowTitleDropdown(true)}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
          />
          {errors.title && <span className="error">{errors.title}</span>}

          {showTitleDropdown && titlesByHeading.length > 0 && (
            <ul className="admin-il-dropdown">
              {titlesByHeading.map((l) => (
                <li
                  key={l.id}
                  onClick={() => {
                    setFormData(l);
                    setEditing(true);
                    setShowTitleDropdown(false);
                  }}
                >
                  {l.title}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* URL */}
        <div className="form-group">
          <label>URL</label>
          <input
            value={formData.url}
            className={errors.url ? "error-input" : ""}
            placeholder="https://example.com"
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          />
          {errors.url && <span className="error">{errors.url}</span>}
        </div>

        <div className="form-actions">
          <button className="primary-btn">{editing ? "Update" : "Add"}</button>
          {editing && (
            <button type="button" className="secondary-btn" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* TABLE */}
      <div className="table-section">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3>Saved Links</h3>
          <div style={{ marginBottom: "16px", display: "flex", justifyContent: "flex-end" }}>
            <TextField
              placeholder="Search..."
              size="small"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: 240,
                backgroundColor: "#fff",
                borderRadius: "8px",
              }}
            />
          </div>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Heading</th>
              <th>Title</th>
              <th>URL</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedLinks
              .filter((item) =>
                item.heading.toLowerCase().includes(searchQuery.toLowerCase())).map((item) => (
                  <tr key={item.id}>
                    <td>{item.heading}</td>
                    <td>{item.title}</td>
                    <td>
                      <a href={item.url} target="_blank" rel="noreferrer">
                        {item.url}
                      </a>
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => {
                          setFormData(item);
                          setEditing(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="danger"
                        onClick={() => setDeleteTarget(item)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
          }}
        >
          <Pagination
            count={Math.ceil(links.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </div>
      </div>

      {/* DELETE MODAL */}
      {deleteTarget && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <h4>Delete Link?</h4>
            <p>
              Are you sure you want to delete <br />
              <strong>{deleteTarget.title}</strong>?
            </p>
            <div className="delete-modal-actions">
              <button
                className="secondary-btn"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button className="danger-btn" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminImportantLinks;
